/** @jsx jsx */

import { Button } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { getBaseAuthUrl } from 'app/developer-v3/utils';
import {
  useAvroEvent,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';

import * as CTAs from './CTAs';
import { getLockedHints, getReadOnlyReasons } from './utils';

type Props = {
  appId: ID;
  hasAuthentication: boolean;
  ordinal: number;
  userCount: number;
  version: string;
};

function CTAAuth({
  appId,
  hasAuthentication,
  ordinal,
  userCount,
  version,
}: Props) {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const {
    isReadOnly,
    adminOnlyHint,
    lifecyclePendingHint,
    productionOrUserCountLimitExceededHint,
  } = getReadOnlyReasons(currentVersionReadOnlyReason, userCount);
  const lockedHints = getLockedHints(userCount);
  const hint = isReadOnly
    ? adminOnlyHint ||
      lifecyclePendingHint ||
      productionOrUserCountLimitExceededHint
    : lockedHints.needAuth;

  const authUrl = hasAuthentication
    ? `${getBaseAuthUrl(appId, version)}/edit`
    : getBaseAuthUrl(appId, version);
  const description =
    'Tell us how to authenticate users with your API. OAuth 2 is most common, but Zapier also supports a handful of options.';
  const learnMoreUrl = `${BASE_DOC_URL_VB}/build/auth`;

  return !isReadOnly && hasAuthentication ? (
    <CTAs.Simple
      action={<Link href={authUrl}>Edit</Link>}
      description={description}
      icon="personCircleFill"
      isActive={false}
      learnMoreUrl={learnMoreUrl}
      ordinal={ordinal}
      title="Authentication"
    />
  ) : (
    <CTAs.Lockable
      action={
        <Button
          aria-label="Set Up Authentication"
          href={authUrl}
          onClick={() => {
            emitPartnerUserInteractionEvent({
              eventAction: 'click',
              interactionGoal: 'Start setting up authentication',
              interactionName: 'integration_home_button_clicked',
              interactionRawText: 'Set Up Authentication',
              optionSelected: 'authentication',
            });
          }}
        >
          Set Up Authentication
        </Button>
      }
      description={description}
      hint={hint}
      icon="personCircleFill"
      isActive={!isReadOnly && !hasAuthentication}
      isLocked={isReadOnly}
      learnMoreUrl={learnMoreUrl}
      ordinal={ordinal}
      title="Authentication"
    />
  );
}

export default CTAAuth;
